<script>
  import PDPVideoPlayer from '@/components/PDPVideoPlayer.vue';
  import Flickity from 'flickity';
  import { bindFlickityHiddenCellInert } from '@/flickityHiddenCellInert.js';
  import { emitter } from '@/main.js';
  import { findVariantImageIndex, debounce, handleViewportSize, buildResponsiveSrcset, buildPdpMobileSlideSrc, getMediaAlt } from '@/lib/vue-helpers.js';

  export default {
    name: 'PDPFreescroll',
    components: {
      PDPVideoPlayer
    },
    props: {
      index: {
        type: Number,
        default: 0
      },
      nonActiveImageOverlayColor: {
        type: String,
        default: null
      },
      nonActiveImageOverlayOpacity: {
        type: Number,
        default: null
      },
      mobileImageSpacing: {
        type: Number,
        default: 10
      },
      mobileNonActiveSlideWidth: {
        type: Number,
        default: 80
      },
      media: {
        type: Array,
        required: true
      },
      isMobile: {
        type: Boolean,
        default: true
      },
      isOutfitPicker: {
        type: Boolean,
        default: false
      },
      zoomEnabled: {
        type: Boolean,
        default: false
      },
      mobileImageWidth: {
        type: Number,
        default: 800
      },
      mobileMaxHeight: {
        type: Number,
        default: 600
      },
      itemsToShow: {
        type: Number,
        default: 1.1
      },
      itemsToShowMobile: {
        type: Number,
        default: null
      },
      arrowGap: {
        type: Number,
        default: null
      },
      showArrows: {
        type: Boolean,
        default: false
      },
      showPagination: {
        type: Boolean,
        default: false
      },
      imagePadding: {
        type: Number,
        default: 5
      },
      handleVideoSize: {
        type: Function,
        default: () => {}
      },
      isMiniPdp: {
        type: Boolean,
        default: false
      },
      useCustomArrows: {
        type: Boolean,
        default: false
      },
      arrowLeftImage: {
        type: String,
        default: ''
      },
      arrowLeftImageAlt: {
        type: String,
        default: ''
      },
      arrowLeftImageWidth: {
        type: [String, Number],
        default: ''
      },
      arrowLeftImageHeight: {
        type: [String, Number],
        default: ''
      },
      arrowLeftImageDisabled: {
        type: String,
        default: ''
      },
      arrowLeftImageDisabledAlt: {
        type: String,
        default: ''
      },
      arrowLeftImageDisabledWidth: {
        type: [String, Number],
        default: ''
      },
      arrowLeftImageDisabledHeight: {
        type: [String, Number],
        default: ''
      },
      nonActiveSlideWidth: {
        type: Number,
        default: 80
      },
      desktopImageWidth: {
        type: Number
      },
      tabletImageWidth: {
        type: Number
      },
      imageBackgroundColor: {
        type: String,
        default: ''
      }
    },
    emits: ['zoom', 'slide-change', 'carousel-ready'],
    data() {
      return {
        flickityInstance: null,
        currentSlide: 0,
        repositionArrowsRaf: null,
        tallestRetry: 0,
        hasEmittedReady: false,
        galleryImageZoomClickSuppressUntil: 0
      };
    },
    computed: {
      shouldShowArrows() {
        if (this.media.length <= 1) return false;
        return this.showArrows || (this.showArrows && this.useCustomArrows && (this.isMiniPdp || this.isOutfitPicker));
      },
      shouldUseCustomArrows() {
        return this.useCustomArrows && (this.isMiniPdp || this.isOutfitPicker) && this.arrowLeftImage;
      },
      slideWidth() {
        if (this.isMobile) {
          const per = this.isOutfitPicker && this.itemsToShowMobile ? this.itemsToShowMobile : this.itemsToShow;
          return `${100 / per}%`;
        } else if (this.isOutfitPicker) {
          return `${100 / this.itemsToShow}%`;
        } else {
          return '100%';
        }
      },
      slideMargin() {
        if (this.isMobile) {
          return `${this.mobileImageSpacing || 0}px`;
        }
        return `${this.imagePadding || 0}px`;
      },
      imageMaxHeight() {
        if (this.isMiniPdp || this.isOutfitPicker) return 'none';
        const cap = Number(this.mobileMaxHeight) || 600;
        return `var(--pdp-mobile-max-h, ${cap}px)`;
      },
      effectiveImageWidth() {
        if ((this.mobileImageWidth && !this.isMiniPdp && !this.isOutfitPicker) || (this.mobileImageWidth && (this.isMiniPdp || this.isOutfitPicker) && this.isMobile)) {
          return `${this.mobileImageWidth}px`;
        } else {
          return 'none';
        }
      },
      paginationMinHeight() {
        if (!this.showPagination || !this.media || this.media.length <= 1 || this.media.length >= 10) {
          return '0px';
        }
        const dotCount = this.media.length;
        if (dotCount <= 5) {
          return '22px';
        } else {
          return '44px';
        }
      },
      paginationDots() {
        if (!this.showPagination || !this.media || this.media.length <= 1 || this.media.length >= 10) {
          return [];
        }
        return this.media;
      },
      imageViewportVars() {
        const styleVars = {};
        if (this.mobileImageWidth) {
          styleVars['--img-mobile-w'] = `${this.mobileImageWidth}px`;
        }
        if (this.tabletImageWidth) {
          styleVars['--img-tablet-w'] = `${this.tabletImageWidth}px`;
        }
        if (this.desktopImageWidth) {
          styleVars['--img-desktop-w'] = `${this.desktopImageWidth}px`;
        }
        return styleVars;
      },
      slideShowObjectFitClass() {
        if (this.isMiniPdp || this.isOutfitPicker) return 'tw-object-cover';
        if (this.isMobile) return 'tw-object-contain';
        return 'tw-object-cover';
      }
    },
    mounted() {
      const isTeleported = this.$el?.parentElement?.id === 'pdp-freescroll-target';
      if (!isTeleported) {
        this.handleViewportSize = handleViewportSize(this);
      } else {
        this.$el.parentElement.classList.add('is-mobile');
      }
      this.isScrolling = false;
      this.debouncedResize = debounce(
        () => {
          if (this.flickityInstance && !this.isScrolling) {
            this.flickityInstance.resize();
          }
        },
        250,
        false
      );
      if (this.$refs.freescrollTrack) {
        this.initFlickity();
      } else {
        this.$nextTick(() => {
          this.initFlickity();
        });
      }
      emitter.on('variant-updated', this.handleVariantUpdate);
      emitter.on('pdp-color-updated', this.bumpGalleryImageZoomClickSuppress);

      if (this.isOutfitPicker) {
        this.$nextTick(() => {
          this.setupArrowRepositioningListener();
          this.calculateTallestSlide();
        });
      }
    },
    beforeUnmount() {
      if (this.handleViewportSize) this.handleViewportSize();
      this.destroyFlickity();
      emitter.off('variant-updated', this.handleVariantUpdate);
      emitter.off('pdp-color-updated', this.bumpGalleryImageZoomClickSuppress);
      if (this.repositionArrowsHandler) {
        window.removeEventListener('resize', this.repositionArrowsHandler);
        this.repositionArrowsHandler = null;
      }
      if (this.repositionArrowsRaf) {
        cancelAnimationFrame(this.repositionArrowsRaf);
        this.repositionArrowsRaf = null;
      }
    },
    watch: {
      media: {
        handler(newVal) {
          if (newVal && newVal.length) {
            this.bumpGalleryImageZoomClickSuppress();
            this.$nextTick(() => {
              this.destroyFlickity();
              this.initFlickity();
            });
          }
        }
      },
      itemsToShow: {
        handler(newVal, oldVal) {
          if (oldVal !== undefined && newVal !== oldVal) {
            this.$nextTick(() => {
              this.destroyFlickity();
              this.initFlickity();
            });
          }
        }
      },
      itemsToShowMobile: {
        handler(newVal, oldVal) {
          if (oldVal !== undefined && newVal !== oldVal) {
            this.$nextTick(() => {
              this.destroyFlickity();
              this.initFlickity();
            });
          }
        }
      },
      showArrows: {
        handler(newVal, oldVal) {
          if (oldVal !== undefined && newVal !== oldVal) {
            this.$nextTick(() => {
              this.destroyFlickity();
              this.initFlickity();
            });
          }
        }
      },
      shouldShowArrows: {
        handler(newVal, oldVal) {
          if (oldVal !== undefined && newVal !== oldVal) {
            this.$nextTick(() => {
              this.destroyFlickity();
              this.initFlickity();
            });
          }
        }
      },
      imagePadding: {
        handler() {
          if (this.isOutfitPicker) {
            this.$nextTick(() => {
              this.calculateTallestSlide();
            });
          }
        }
      }
    },
    methods: {
      getMediaAlt,
      setImageMobileSize(imgUrl) {
        return buildPdpMobileSlideSrc(imgUrl, this.mobileImageWidth);
      },
      slideImgIntrinsicDims(image) {
        if (!image?.width || !image?.height) return null;
        if (!this.isMobile || this.isMiniPdp || this.isOutfitPicker) {
          return { w: image.width, h: image.height };
        }
        const base = Number(this.mobileImageWidth) || 400;
        const w = base * 2;
        const h = Math.round((w * image.height) / image.width);
        return { w, h };
      },
      slideImgWidth(image) {
        const d = this.slideImgIntrinsicDims(image);
        return d ? d.w : undefined;
      },
      slideImgHeight(image) {
        const d = this.slideImgIntrinsicDims(image);
        return d ? d.h : undefined;
      },
      readPdpMobileHeightBounds() {
        const root = document.getElementById('product-page');
        if (!root) return { min: 0, max: 0 };
        const s = getComputedStyle(root);
        const min = parseFloat(s.getPropertyValue('--pdp-mobile-min-h')) || 0;
        const max = parseFloat(s.getPropertyValue('--pdp-mobile-max-h')) || 0;
        return { min, max };
      },
      getMobileImageSrcset(imgUrl) {
        return buildResponsiveSrcset(imgUrl, this.mobileImageWidth);
      },
      handleZoom(productImageUrl) {
        this.$emit('zoom', productImageUrl);
      },
      bumpGalleryImageZoomClickSuppress() {
        this.galleryImageZoomClickSuppressUntil = Date.now() + 450;
      },
      handleGalleryImageClick(ev, productImageUrl) {
        if (Date.now() < this.galleryImageZoomClickSuppressUntil) {
          if (ev) {
            ev.preventDefault();
            ev.stopPropagation();
          }
          return;
        }
        this.handleZoom(productImageUrl);
      },
      setupCustomArrows() {
        if (!this.$refs.freescrollTrack || !this.shouldUseCustomArrows) return;

        const prevButton = this.$refs.freescrollTrack.querySelector('.flickity-prev-next-button.previous');
        const nextButton = this.$refs.freescrollTrack.querySelector('.flickity-prev-next-button.next');

        if (prevButton) {
          prevButton.innerHTML = this.createCustomArrowHTML('Previous');
        }

        if (nextButton) {
          nextButton.innerHTML = this.createCustomArrowHTML('Next');
        }
      },
      handleVariantUpdate(e) {
        this.bumpGalleryImageZoomClickSuppress();
        if (this.isOutfitPicker) {
          return;
        }
        const variantImage = e.image;
        const variantWidth = e.width;
        const resetToFirst = e.resetToFirst;

        const loggingEnabled = document.getElementById('product-families-json-data')?.dataset.speedTracking === 'true' || Boolean(window.productFamiliesDataDebug);

        if (!this.media || !this.media.length) {
          if (loggingEnabled) console.log('[SmartTags][Freescroll] No media available', { variantImage, variantWidth, resetToFirst });
          return;
        }

        const slideIndex = resetToFirst ? 0 : findVariantImageIndex(this.media, variantImage, variantWidth, e.preferWidthSlug === true);

        if (loggingEnabled) console.log('[SmartTags][Freescroll] Variant update', { variantImage, variantWidth, resetToFirst, preferWidthSlug: e.preferWidthSlug === true, slideIndex, mediaCount: this.media.length });

        if (slideIndex !== -1) {
          this.goToSlide(slideIndex);
        }
      },
      createCustomArrowHTML(direction) {
        if (!this.arrowLeftImage) return '';

        const baseImageProps = {
          src: this.arrowLeftImage,
          alt: this.arrowLeftImageAlt || direction,
          width: this.arrowLeftImageWidth,
          height: this.arrowLeftImageHeight
        };

        const disabledImageProps = this.arrowLeftImageDisabled
          ? {
              src: this.arrowLeftImageDisabled,
              alt: this.arrowLeftImageDisabledAlt || `${direction} (Disabled)`,
              width: this.arrowLeftImageDisabledWidth || this.arrowLeftImageWidth,
              height: this.arrowLeftImageDisabledHeight || this.arrowLeftImageHeight
            }
          : null;

        return `
          <img
            src="${baseImageProps.src}" 
            alt="${baseImageProps.alt}"
            ${baseImageProps.width ? `width="${baseImageProps.width}"` : ''}
            ${baseImageProps.height ? `height="${baseImageProps.height}"` : ''}
          />
          ${
            disabledImageProps
              ? `
            <img
              src="${disabledImageProps.src}"
              alt="${disabledImageProps.alt}"
              ${disabledImageProps.width ? `width="${disabledImageProps.width}"` : ''}
              ${disabledImageProps.height ? `height="${disabledImageProps.height}"` : ''}
              class="disabled-arrow"
            />
          `
              : ''
          }`;
      },
      initFlickity() {
        if (!this.$refs.freescrollTrack) return;

        const options = {
          wrapAround: true,
          pageDots: false,
          prevNextButtons: this.shouldShowArrows,
          cellAlign: 'center',
          draggable: true,
          accessibility: true,
          adaptiveHeight: !this.isMobile || this.isOutfitPicker,
          selectedAttraction: 0.09,
          freeScroll: false,
          dragThreshold: 20,
          friction: 0.4,
          contain: this.shouldShowArrows && !(this.isOutfitPicker && this.imagePadding < 0),
          setGallerySize: true,
          percentPosition: false,
          imagesLoaded: !!(this.isMobile && !this.isMiniPdp && !this.isOutfitPicker),
          initialIndex: 0,
          groupCells: this.shouldShowArrows ? 1 : false,
          on: {
            ready: () => {
              this.handleMediaLoad();
              this.$nextTick(() => {
                if (this.flickityInstance && this.shouldShowArrows) {
                  this.forceProperSlideWidth();
                }
                this.ensureViewportHeight();
                if (this.flickityInstance) {
                  this.flickityInstance.resize();
                  this.flickityInstance.select(0, false, true);
                  this.revealSlideshow();
                }
                if (this.isOutfitPicker) {
                  this.applyBackgroundColorStyles();
                  this.scheduleRepositionArrows();
                  this.calculateTallestSlide();
                }
                if (this.shouldUseCustomArrows) {
                  this.setupCustomArrows();
                }
              });
            },
            dragStart: () => {
              this.isScrolling = true;
            },
            dragEnd: () => {
              setTimeout(() => {
                this.isScrolling = false;
              }, 300);
            },
            change: index => {
              this.currentSlide = index;
              const product = this.media && this.media[index] ? this.media[index] : null;
              this.$emit('slide-change', { index, product });
            },
            settle: () => {
              requestAnimationFrame(() => {
                if (this.flickityInstance) {
                  this.flickityInstance.reposition();
                }
              });
              if (this.isOutfitPicker) {
                this.$nextTick(() => {
                  this.scheduleRepositionArrows();
                  this.calculateTallestSlide();
                });
              }
            }
          }
        };

        this.flickityInstance = new Flickity(this.$refs.freescrollTrack, options);
        bindFlickityHiddenCellInert(this.flickityInstance);

        setTimeout(() => {
          if (this.$refs.freescrollTrack && this.$refs.freescrollTrack.parentElement && !this.$refs.freescrollTrack.parentElement.classList.contains('flickity-ready')) {
            if (this.flickityInstance) {
              this.flickityInstance.select(0, false, true);
            }
            this.revealSlideshow();
          }
        }, 100);
      },
      destroyFlickity() {
        if (this.flickityInstance) {
          this.flickityInstance.destroy();
          this.flickityInstance = null;
          this.currentSlide = 0;
        }
        this.hasEmittedReady = false;
      },
      updateFlickitySize() {
        if (this.flickityInstance) {
          this.flickityInstance.resize();
          if (this.shouldShowArrows) {
            this.$nextTick(() => {
              this.forceProperSlideWidth();
            });
          }
          if (this.isOutfitPicker) {
            this.$nextTick(() => this.calculateTallestSlide());
          }
        }
      },
      ensureViewportHeight() {
        if (!this.$refs.freescrollTrack) return false;
        const viewport = this.$refs.freescrollTrack.querySelector('.flickity-viewport');
        const selectedSlide = this.flickityInstance?.selectedElement || this.$refs.freescrollTrack.querySelector('.freescroll__slide');
        const selectedImage = selectedSlide?.querySelector('img.slideShow');
        if (!viewport || !selectedSlide || !selectedImage) return false;

        const imageWidth = selectedImage.naturalWidth || parseFloat(selectedImage.getAttribute('width')) || 0;
        const imageHeight = selectedImage.naturalHeight || parseFloat(selectedImage.getAttribute('height')) || 0;
        const slideWidth = selectedSlide.clientWidth || viewport.clientWidth || selectedImage.clientWidth || 0;
        if (!imageWidth || !imageHeight || !slideWidth) return false;

        let measuredHeight = Math.round(slideWidth * (imageHeight / imageWidth));
        if (!measuredHeight) return false;

        if (this.isMobile && !this.isMiniPdp && !this.isOutfitPicker) {
          const bounds = this.readPdpMobileHeightBounds();
          if (bounds.min > 0) measuredHeight = Math.max(measuredHeight, bounds.min);
          if (bounds.max > 0) measuredHeight = Math.min(measuredHeight, bounds.max);
        }

        const next = `${measuredHeight}px`;
        if (viewport.style.height === next) return false;
        viewport.style.height = next;
        return true;
      },
      goToSlide(index) {
        if (!this.flickityInstance || index < 0 || index >= this.media.length) return;
        if (this.currentSlide === index) return;
        this.flickityInstance.select(index);
        const product = this.media && this.media[index] ? this.media[index] : null;
        this.$emit('slide-change', { index, product });
      },
      handleMediaLoad() {
        if (this.handleVideoSize && typeof this.handleVideoSize === 'function') {
          this.handleVideoSize();
        }
        this.$nextTick(() => {
          this.ensureViewportHeight();
          this.updateFlickitySize();
          this.emitReadyIfMeasured();
        });
        if (!this.isScrolling && this.debouncedResize && typeof this.debouncedResize === 'function') {
          this.debouncedResize();
        }
        if (this.isOutfitPicker) {
          this.$nextTick(() => this.calculateTallestSlide());
        }
      },
      emitReadyIfMeasured() {
        if (this.hasEmittedReady || !this.$refs.freescrollTrack) return;
        const viewport = this.$refs.freescrollTrack.querySelector('.flickity-viewport');
        if (viewport && viewport.offsetHeight <= 0) {
          this.ensureViewportHeight();
        }
        if (!viewport || viewport.offsetHeight <= 0) return;
        this.hasEmittedReady = true;
        this.$emit('carousel-ready');
      },
      scheduleRepositionArrows() {
        if (this.repositionArrowsRaf) cancelAnimationFrame(this.repositionArrowsRaf);
        this.repositionArrowsRaf = requestAnimationFrame(() => {
          this.repositionArrowsRaf = null;
          this.repositionArrowsAroundActiveSlide();
        });
      },
      repositionArrowsAroundActiveSlide() {
        if (!this.isOutfitPicker || !this.shouldShowArrows) return;
        if (!this.$refs.freescrollTrack || !this.flickityInstance) return;

        const container = this.$refs.freescrollTrack;
        const prevButton = container.querySelector('.flickity-prev-next-button.previous');
        const nextButton = container.querySelector('.flickity-prev-next-button.next');
        const activeSlide = this.flickityInstance.selectedElement;
        if (!prevButton || !nextButton || !activeSlide) return;

        const containerRect = container.getBoundingClientRect();
        const activeRect = activeSlide.getBoundingClientRect();

        const buttonPrevRect = prevButton.getBoundingClientRect();
        const buttonNextRect = nextButton.getBoundingClientRect();

        const gap = Number(this.arrowGap);

        let leftForPrev = Math.max(activeRect.left - containerRect.left - buttonPrevRect.width - gap, 0);
        let leftForNext = activeRect.right - containerRect.left + gap;

        leftForPrev = Math.min(Math.max(leftForPrev, 0), containerRect.width - buttonPrevRect.width);
        leftForNext = Math.min(Math.max(leftForNext, 0), containerRect.width - buttonNextRect.width);

        prevButton.style.left = `${leftForPrev}px`;
        prevButton.style.right = 'auto';
        nextButton.style.left = `${leftForNext}px`;
        nextButton.style.right = 'auto';
      },
      setupArrowRepositioningListener() {
        if (!this.isOutfitPicker || !this.shouldShowArrows) return;
        if (!this._repositionArrowsHandler) {
          this._repositionArrowsHandler = () => {
            this.scheduleRepositionArrows();
            this.calculateTallestSlide();
          };
          window.addEventListener('resize', this._repositionArrowsHandler);
        }
        this.$nextTick(() => this.scheduleRepositionArrows());
      },
      calculateTallestSlide() {
        if (!this.$refs.freescrollTrack) return;
        const slides = this.$refs.freescrollTrack.querySelectorAll('.freescroll__slide');
        if (!slides || !slides.length) return;

        let tallestHeight = 0;
        slides.forEach(slide => {
          const slideHeight = slide.offsetHeight;
          if (slideHeight > tallestHeight) {
            tallestHeight = slideHeight;
          }
        });

        if (!tallestHeight || tallestHeight === 0) {
          if (this.tallestRetry < 10) {
            this.tallestRetry += 1;
            setTimeout(() => {
              this.calculateTallestSlide();
            }, 120);
          }
          return;
        }

        this.tallestRetry = 0;
        slides.forEach(slide => {
          slide.style.height = `${tallestHeight}px`;
        });
      },
      applyBackgroundColorStyles(color) {
        const track = this.$refs.freescrollTrack;
        if (!track) return;
        const value = typeof color === 'string' ? color : this.imageBackgroundColor;
        if (value) {
          track.style.setProperty('--slide-bg', value);
        } else {
          track.style.removeProperty('--slide-bg');
        }
        if (this.nonActiveImageOverlayColor) {
          track.style.setProperty('--non-active-image-overlay-color', this.nonActiveImageOverlayColor);
        } else {
          track.style.removeProperty('--non-active-image-overlay-color');
        }
        if (this.nonActiveImageOverlayOpacity !== undefined && this.nonActiveImageOverlayOpacity !== null && this.nonActiveImageOverlayOpacity !== '') {
          track.style.setProperty('--non-active-image-overlay-opacity', String(this.nonActiveImageOverlayOpacity));
        } else {
          track.style.removeProperty('--non-active-image-overlay-opacity');
        }
      },
      revealSlideshow() {
        if (this.flickityInstance) {
          this.flickityInstance.reposition();
          if (this.flickityInstance.selectedIndex !== 0) {
            this.flickityInstance.select(0, false, true);
          }
        }

        if (this.isMobile && !this.isMiniPdp && !this.isOutfitPicker) {
          this.ensureViewportHeight();
          if (this.flickityInstance) this.flickityInstance.resize();
        }

        if (this.$refs.freescrollTrack && this.$refs.freescrollTrack.parentElement) {
          this.$refs.freescrollTrack.parentElement.classList.add('flickity-ready');
        }

        const pdpImages = document.getElementById('PDPImages');
        if (pdpImages) pdpImages.style.minHeight = '';
        const mobileCarousel = document.querySelector('.product-page .mobile-carousel-block');
        if (mobileCarousel) mobileCarousel.style.minHeight = '';
        this.emitReadyIfMeasured();
      },
      forceProperSlideWidth() {
        if (!this.$refs.freescrollTrack || !this.shouldShowArrows) return;
        if (this.isOutfitPicker) return;

        const slides = this.$refs.freescrollTrack.querySelectorAll('.freescroll__slide');
        const viewport = this.$refs.freescrollTrack.querySelector('.flickity-viewport');

        if (viewport && slides.length > 0) {
          const viewportWidth = viewport.offsetWidth;
          slides.forEach(slide => {
            slide.style.width = `${viewportWidth}px`;
            slide.style.marginRight = '0px';
          });
        }
      }
    }
  };
</script>

<template>
  <div v-if="media.length" :class="['pdp-freescroll', `pdp-freescroll-index-${index}`, { 'is-mini-pdp': isMiniPdp, 'is-outfit-picker': isOutfitPicker }]">
    <div class="freescroll-track" :class="{ 'has-arrows': shouldShowArrows, 'has-negative-spacing': isOutfitPicker }" ref="freescrollTrack">
      <div
        v-for="(product, index) in media"
        :key="'product-' + index + '-' + '-freescroll'"
        :pid="product.node.id"
        class="freescroll__slide"
        :style="{
          width: slideWidth,
          marginRight: slideMargin,
          maxWidth: effectiveImageWidth
        }"
      >
        <img v-if="product.node.image" :src="setImageMobileSize(product.node.image.url)" :srcset="getMobileImageSrcset(product.node.image.url)" class="slideShow tw-block tw-w-full" :class="slideShowObjectFitClass" :style="[{ width: isOutfitPicker && index !== currentSlide ? `${this.isMobile ? this.mobileNonActiveSlideWidth : this.nonActiveSlideWidth}%` : '100%' }, imageViewportVars]" :alt="getMediaAlt(product)" :width="slideImgWidth(product.node.image)" :height="slideImgHeight(product.node.image)" @load="handleMediaLoad" @click="handleGalleryImageClick($event, product.node.image.url)" :loading="index === 0 ? 'eager' : 'lazy'" :fetchpriority="index === 0 ? 'high' : 'auto'" decoding="async" />
        <PDPVideoPlayer v-if="product.node.sources" :sources="product.node.sources" :productID="product.node.id" :handleVideoSize="handleVideoSize" />
        <div v-if="zoomEnabled && product.node.image" class="media__buttons__holder">
          <button class="media__button media__button--zoom" data-zoom-button @click="handleZoom(product.node.image.url)">
            <svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-search" viewBox="0 0 16 16">
              <path d="M16 14.864 14.863 16l-4.24-4.241a6.406 6.406 0 0 1-4.048 1.392 6.61 6.61 0 0 1-4.65-1.925A6.493 6.493 0 0 1 .5 9.098 6.51 6.51 0 0 1 0 6.575a6.536 6.536 0 0 1 1.922-4.652A6.62 6.62 0 0 1 4.062.5 6.52 6.52 0 0 1 6.575 0 6.5 6.5 0 0 1 9.1.5c.8.332 1.51.806 2.129 1.423a6.454 6.454 0 0 1 1.436 2.13 6.54 6.54 0 0 1 .498 2.522c0 1.503-.468 2.853-1.4 4.048L16 14.864zM3.053 10.091c.973.972 2.147 1.461 3.522 1.461 1.378 0 2.551-.489 3.525-1.461.968-.967 1.45-2.138 1.45-3.514 0-1.37-.482-2.545-1.45-3.524-.981-.968-2.154-1.45-3.525-1.45-1.376 0-2.547.483-3.513 1.45-.973.973-1.46 2.146-1.46 3.523 0 1.375.483 2.548 1.45 3.515z" />
            </svg>
            <span class="visually-hidden"> Zoom in image {{ index + 1 }} of {{ media.length }} </span>
          </button>
        </div>
      </div>
    </div>

    <!-- Always render container to prevent CLS, reserve space based on dot count -->
    <div class="freescroll-pagination" :class="{ 'is-hidden': !showPagination || !media || media.length <= 1 || media.length >= 10 }" :style="{ minHeight: paginationMinHeight }">
      <button v-for="(_, index) in paginationDots" :key="'dot-' + index" :class="['freescroll-pagination__dot', { 'is-selected': currentSlide === index }]" @click="goToSlide(index)" :aria-label="`Go to slide ${index + 1}`"></button>
    </div>
  </div>
</template>

<style>
  /* Mobile Freescroll Styles */
  .pdp-freescroll {
    width: 100%;
    max-width: var(--LAYOUT-WIDTH, 100%);
    margin: 0 auto;
    padding-right: 0;
    touch-action: pan-y;
    pointer-events: all;
    display: block;
    position: relative;
    opacity: 0;
    transition: opacity 0.15s ease-in;
  }

  .pdp-freescroll .freescroll-track:not(.flickity-enabled) {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    overflow-x: hidden;
    overflow-y: visible;
    align-items: stretch;
    min-height: var(--pdp-mobile-min-h, 0);
  }

  .pdp-freescroll.flickity-ready {
    opacity: 1;
  }

  .pdp-freescroll.is-mini-pdp {
    display: flex;
    flex-direction: column;
    justify-content: center;
    overflow: hidden;
    max-width: none;
    padding: 0;
  }

  .pdp-freescroll.is-outfit-picker {
    display: block;
    /* overflow: hidden; */
    max-width: none;
    padding: 0;
  }

  .pdp-freescroll.is-mini-pdp .freescroll-track {
    margin: 0;
  }

  .pdp-freescroll.is-mini-pdp .freescroll-track.freescroll__slide {
    width: 100%;
    margin-right: 0;
  }

  .pdp-freescroll.is-mini-pdp .freescroll-track .flickity-button.previous,
  .pdp-freescroll.is-mini-pdp .freescroll-track .flickity-button.next {
    background: none;
    border: none;
    padding: 0;
  }

  .pdp-freescroll.is-mini-pdp .freescroll-track .flickity-button.previous img,
  .pdp-freescroll.is-mini-pdp .freescroll-track .flickity-button.next img {
    display: block;
    max-width: 100%;
    height: auto;
  }

  .pdp-freescroll.is-mini-pdp .freescroll-track .flickity-button.previous img.disabled-arrow,
  .pdp-freescroll.is-mini-pdp .freescroll-track .flickity-button.next img.disabled-arrow,
  .pdp-freescroll.is-outfit-picker .freescroll-track .flickity-button.previous img.disabled-arrow,
  .pdp-freescroll.is-outfit-picker .freescroll-track .flickity-button.next img.disabled-arrow {
    display: none;
  }

  .pdp-freescroll.is-mini-pdp .freescroll-track .flickity-button.previous.is-disabled,
  .pdp-freescroll.is-mini-pdp .freescroll-track .flickity-button.next.is-disabled {
    opacity: 1;
    pointer-events: none;
    cursor: not-allowed;
  }

  .pdp-freescroll.is-mini-pdp .freescroll-track .flickity-button.previous.is-disabled img:not(.disabled-arrow),
  .pdp-freescroll.is-mini-pdp .freescroll-track .flickity-button.next.is-disabled img:not(.disabled-arrow),
  .pdp-freescroll.is-outfit-picker .freescroll-track .flickity-button.previous.is-disabled img:not(.disabled-arrow),
  .pdp-freescroll.is-outfit-picker .freescroll-track .flickity-button.next.is-disabled img:not(.disabled-arrow) {
    display: none;
  }

  .pdp-freescroll.is-mini-pdp .freescroll-track .flickity-button.previous.is-disabled img.disabled-arrow,
  .pdp-freescroll.is-mini-pdp .freescroll-track .flickity-button.next.is-disabled img.disabled-arrow,
  .pdp-freescroll.is-outfit-picker .freescroll-track .flickity-button.previous.is-disabled img.disabled-arrow,
  .pdp-freescroll.is-outfit-picker .freescroll-track .flickity-button.next.is-disabled img.disabled-arrow {
    display: block;
  }

  .pdp-freescroll.is-mini-pdp .freescroll-track .flickity-button.previous:focus:not(:focus-visible),
  .pdp-freescroll.is-mini-pdp .freescroll-track .flickity-button.next:focus:not(:focus-visible),
  .pdp-freescroll.is-outfit-picker .freescroll-track .flickity-button.next:focus:not(:focus-visible),
  .pdp-freescroll.is-outfit-picker .freescroll-track .flickity-button.previous:focus:not(:focus-visible) {
    outline: none;
    box-shadow: none;
  }

  .pdp-freescroll.is-mini-pdp .freescroll-track .flickity-button.next img {
    transform: rotate(180deg);
  }

  .is-mobile .pdp-freescroll {
    display: block;
  }
  .is-mobile .pdp-freescroll.is-outfit-picker {
    display: block;
    /* overflow: hidden; */
    max-width: none;
    padding: 0;
  }
  .is-mobile .pdp-freescroll.is-mini-pdp .freescroll-track {
    margin: 0;
  }
  .is-mobile .pdp-freescroll.is-mini-pdp .freescroll-track.freescroll__slide {
    width: 100%;
    margin-right: 0;
  }
  .is-mobile .pdp-freescroll.is-mini-pdp .freescroll-track .flickity-button.previous,
  .is-mobile .pdp-freescroll.is-mini-pdp .freescroll-track .flickity-button.next {
    background: none;
    border: none;
    padding: 0;
  }
  .is-mobile .pdp-freescroll.is-mini-pdp .freescroll-track .flickity-button.previous img,
  .is-mobile .pdp-freescroll.is-mini-pdp .freescroll-track .flickity-button.next img,
  .is-mobile .pdp-freescroll.is-outfit-picker .freescroll-track .flickity-button.next img,
  .is-mobile .pdp-freescroll.is-outfit-picker .freescroll-track .flickity-button.previous img {
    display: block;
    max-width: 100%;
    height: auto;
  }
  .is-mobile .pdp-freescroll.is-mini-pdp .freescroll-track .flickity-button.previous img.disabled-arrow,
  .is-mobile .pdp-freescroll.is-mini-pdp .freescroll-track .flickity-button.next img.disabled-arrow,
  .is-mobile .pdp-freescroll.is-outfit-picker .freescroll-track .flickity-button.previous img.disabled-arrow,
  .is-mobile .pdp-freescroll.is-outfit-picker .freescroll-track .flickity-button.next img.disabled-arrow {
    display: none;
  }

  .pdp-freescroll.is-mini-pdp .freescroll-track .flickity-button.previous.is-disabled,
  .pdp-freescroll.is-mini-pdp .freescroll-track .flickity-button.next.is-disabled,
  .pdp-freescroll.is-outfit-picker .freescroll-track .flickity-button.previous.is-disabled,
  .pdp-freescroll.is-outfit-picker .freescroll-track .flickity-button.next.is-disabled {
    opacity: 1;
    pointer-events: none;
    cursor: not-allowed;
  }

  .pdp-freescroll.is-mini-pdp .freescroll-track .flickity-button.previous.is-disabled img:not(.disabled-arrow),
  .pdp-freescroll.is-mini-pdp .freescroll-track .flickity-button.next.is-disabled img:not(.disabled-arrow),
  .pdp-freescroll.is-outfit-picker .freescroll-track .flickity-button.previous.is-disabled img:not(.disabled-arrow),
  .pdp-freescroll.is-outfit-picker .freescroll-track .flickity-button.next.is-disabled img:not(.disabled-arrow) {
    display: none;
  }

  .pdp-freescroll.is-mini-pdp .freescroll-track .flickity-button.previous.is-disabled img.disabled-arrow,
  .pdp-freescroll.is-mini-pdp .freescroll-track .flickity-button.next.is-disabled img.disabled-arrow,
  .pdp-freescroll.is-outfit-picker .freescroll-track .flickity-button.previous.is-disabled img.disabled-arrow,
  .pdp-freescroll.is-outfit-picker .freescroll-track .flickity-button.next.is-disabled img.disabled-arrow {
    display: block;
  }

  .pdp-freescroll.is-mini-pdp .freescroll-track .flickity-button.previous:focus:not(:focus-visible),
  .pdp-freescroll.is-mini-pdp .freescroll-track .flickity-button.next:focus:not(:focus-visible),
  .pdp-freescroll.is-outfit-picker .freescroll-track .flickity-button.previous:focus:not(:focus-visible),
  .pdp-freescroll.is-outfit-picker .freescroll-track .flickity-button.next:focus:not(:focus-visible) {
    outline: none;
    box-shadow: none;
  }

  .pdp-freescroll.is-mini-pdp .freescroll-track .flickity-button.next img,
  .pdp-freescroll.is-outfit-picker .freescroll-track .flickity-button.next img {
    transform: rotate(180deg);
  }

  .pdp-freescroll .freescroll__slide img {
    width: 100%;
    height: auto;
    max-height: 100%;
    object-fit: contain;
    display: block;
    backface-visibility: hidden;
    opacity: 1;
    transition: opacity 0.1s ease-in-out;
  }

  .pdp-freescroll .flickity-enabled .flickity-viewport {
    overflow: hidden;
    padding-bottom: 0 !important;
    opacity: 1;
  }

  .pdp-freescroll .freescroll__slide {
    position: relative;
    width: v-bind(slideWidth);
    margin-right: v-bind(slideMargin);
    max-height: v-bind(imageMaxHeight);
    max-width: v-bind(effectiveImageWidth);
    display: flex;
    align-items: center;
    justify-content: center;
    height: auto;
  }
  .freescroll__slide img {
    width: 100%;
    height: auto;
    max-height: 100%;
    object-fit: contain;
  }
  .pdp-freescroll .flickity-enabled .flickity-slider {
    height: auto !important;
    will-change: transform;
  }

  .pdp-freescroll .flickity-enabled .flickity-button {
    background: none;
    border: none;
    padding: 0;
    transform: none;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 10;
  }

  .pdp-freescroll .flickity-enabled .flickity-button:disabled {
    opacity: 1;
    cursor: not-allowed;
  }

  .pdp-freescroll .flickity-enabled .flickity-button:focus:not(:focus-visible) {
    outline: none;
    box-shadow: none;
  }

  .pdp-freescroll .flickity-enabled .flickity-button img {
    display: block;
    width: auto;
    height: auto;
    max-width: 100%;
    max-height: 100%;
  }

  .pdp-freescroll .flickity-enabled .flickity-button.next img {
    transform: rotate(180deg);
  }

  .pdp-freescroll .flickity-prev-next-button {
    width: 40px;
    height: 40px;
  }

  .pdp-freescroll .flickity-prev-next-button svg {
    display: block;
    fill: #333;
  }

  .pdp-freescroll .freescroll-pagination {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    margin-top: 15px;
    box-sizing: border-box;
    overflow: hidden;
    transition:
      min-height 0.15s ease-out,
      opacity 0.15s ease-out;
  }

  .pdp-freescroll .freescroll-pagination.is-hidden {
    display: flex;
    min-height: 0 !important;
    margin-top: 0;
    opacity: 0;
  }

  .pdp-freescroll .freescroll-pagination__dot {
    margin: 5px;
    height: 2px;
    width: 20px;
    background-color: #d9d9d9;
    border: none;
    cursor: pointer;
    padding: 0;
    transition: background-color 0.2s ease;
  }

  .pdp-freescroll .freescroll-pagination__dot.is-selected {
    background-color: var(--text);
    filter: brightness(0.5);
  }

  .pdp-freescroll .freescroll-pagination__dot:hover {
    background-color: var(--text);
    opacity: 0.7;
  }

  .pdp-freescroll.is-mini-pdp .freescroll-pagination.is-hidden {
    min-height: 0 !important;
    margin-top: 0;
  }

  .pdp-freescroll.is-outfit-picker .freescroll__slide {
    display: flex;
    align-items: center;
    justify-content: center;
    height: auto;
  }
  .pdp-freescroll.is-outfit-picker .freescroll__slide img {
    width: 100%;
    height: auto;
    max-height: 100%;
    object-fit: contain;
  }

  .pdp-freescroll.is-outfit-picker .freescroll-track.has-negative-spacing .freescroll__slide {
    overflow: visible;
    z-index: 1;
  }

  .pdp-freescroll.is-outfit-picker .freescroll-track.has-negative-spacing .freescroll__slide.is-selected {
    z-index: 2;
  }

  .pdp-freescroll.is-outfit-picker .freescroll-track.has-negative-spacing .flickity-viewport,
  .pdp-freescroll.is-outfit-picker .freescroll-track.has-negative-spacing .flickity-slider {
    overflow: visible;
  }

  .pdp-freescroll.is-outfit-picker .freescroll__slide::after {
    content: '';
    position: absolute;
    inset: 0;
    background-color: var(--non-active-image-overlay-color, transparent);
    opacity: 0;
    pointer-events: none;
    z-index: 1;
    transition: opacity 0.2s ease;
  }

  .pdp-freescroll.is-outfit-picker .freescroll__slide:not(.is-selected)::after {
    opacity: calc(var(--non-active-image-overlay-opacity, 0) * 0.01);
  }

  .pdp-freescroll.is-outfit-picker .freescroll-track,
  .pdp-freescroll.is-outfit-picker .flickity-enabled .flickity-viewport,
  .pdp-freescroll.is-outfit-picker .freescroll__slide {
    background-color: var(--slide-bg, transparent);
  }

  .is-mobile .pdp-freescroll .freescroll__slide img {
    max-width: var(--img-mobile-w, none);
  }
  .is-tablet .pdp-freescroll .freescroll__slide img {
    max-width: var(--img-tablet-w, none);
  }
  .is-desktop .pdp-freescroll .freescroll__slide img {
    max-width: var(--img-desktop-w, none);
  }
</style>
